import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Calendar, Clock, Users, Video, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
    const [step, setStep] = useState(1); // 1 pick type, 2 pick time, 3 client info
    const [consultations, setConsultations] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    const [slotData, setSlotData] = useState({ slots: [], loading: false });
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        axios.get(`${API}/consultations`)
            .then((r) => setConsultations(r.data))
            .catch(() => toast.error("Could not load consultations."));
    }, []);

    // When user picks a type, load slots
    useEffect(() => {
        if (!selectedType) return;
        setSlotData({ slots: [], loading: true });
        axios.get(`${API}/consultations/${selectedType.id}/slots`)
            .then((r) => setSlotData({ slots: r.data.slots, loading: false }))
            .catch(() => setSlotData({ slots: [], loading: false }));
    }, [selectedType]);

    const slotsByDate = useMemo(() => {
        const map = {};
        slotData.slots.forEach((s) => {
            map[s.date] = map[s.date] || [];
            map[s.date].push(s);
        });
        return map;
    }, [slotData.slots]);

    const dates = Object.keys(slotsByDate).sort();

    const pickType = (t) => {
        setSelectedType(t);
        setSelectedSlot(null);
        setSelectedDate(null);
        setStep(2);
    };

    const pickSlot = (s) => {
        setSelectedSlot(s);
        setStep(3);
    };

    return (
        <main className="bg-[#fbf9f8] min-h-screen pt-32 pb-24" data-testid="booking-page">
            <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#e47a6a] mb-4">Book a Strategy Call</p>
                <h1 className="font-serif text-4xl md:text-6xl text-[#0c0f3d] leading-[1] tracking-tight">
                    Let&apos;s move your <span className="italic text-[#e47a6a]">mission</span> forward.
                </h1>
                <p className="font-sans text-base text-[#0c0f3d]/75 mt-6 max-w-[640px] leading-[1.8]">
                    Choose the call type that fits your organization, pick a time that works, and secure your spot. All calls take place on Zoom and the details will arrive in your inbox.
                </p>

                {/* Steps */}
                <div className="mt-12 -mx-6 md:mx-0 px-6 md:px-0 overflow-x-auto">
                    <div className="flex items-center gap-3 min-w-max md:min-w-0">
                        <StepDot num={1} label="Choose call type" active={step >= 1} current={step === 1} />
                        <div className="flex-1 h-px bg-[#e3c7b9] min-w-[24px]" />
                        <StepDot num={2} label="Pick a time" active={step >= 2} current={step === 2} />
                        <div className="flex-1 h-px bg-[#e3c7b9] min-w-[24px]" />
                        <StepDot num={3} label="Confirm" active={step >= 3} current={step === 3} />
                    </div>
                </div>

                <div className="mt-12">
                    {step === 1 && <StepChooseType consultations={consultations} onPick={pickType} />}
                    {step === 2 && selectedType && (
                        <StepPickTime
                            type={selectedType}
                            slotsByDate={slotsByDate}
                            dates={dates}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            onPickSlot={pickSlot}
                            onBack={() => setStep(1)}
                            loading={slotData.loading}
                        />
                    )}
                    {step === 3 && selectedType && selectedSlot && (
                        <StepConfirm
                            type={selectedType}
                            slot={selectedSlot}
                            onBack={() => setStep(2)}
                            submitting={submitting}
                            setSubmitting={setSubmitting}
                        />
                    )}
                </div>
            </div>
        </main>
    );
}

function StepDot({ num, label, active, current }) {
    return (
        <div className="flex items-center gap-3 flex-shrink-0" data-testid={`booking-step-${num}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${current ? "bg-[#e47a6a] text-white" : active ? "bg-[#0c0f3d] text-white" : "bg-[#e3c7b9]/40 text-[#0c0f3d]/50"}`}>
                {num}
            </div>
            <span className={`font-sans text-xs uppercase tracking-[0.2em] ${current ? "text-[#e47a6a]" : "text-[#0c0f3d]/60"}`}>
                {label}
            </span>
        </div>
    );
}

function StepChooseType({ consultations, onPick }) {
    if (consultations.length === 0) {
        return <p className="font-serif italic text-xl text-[#0c0f3d]">Loading options…</p>;
    }
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="booking-types">
            {consultations.map((c) => (
                <button
                    key={c.id}
                    onClick={() => onPick(c)}
                    data-testid={`booking-type-${c.id}`}
                    className="text-left bg-white border border-[#e3c7b9] p-8 hover:border-[#0c0f3d] transition-colors group"
                >
                    <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#e47a6a] mb-4">{c.for_audience || "Strategy call"}</p>
                    <h3 className="font-serif text-2xl text-[#0c0f3d] leading-tight mb-3">{c.name}</h3>
                    <p className="font-sans text-sm text-[#0c0f3d]/70 leading-[1.7] mb-6">{c.description}</p>
                    <div className="flex items-center justify-between border-t border-[#e3c7b9] pt-6">
                        <div className="flex items-center gap-1.5">
                            <Clock size={14} className="text-[#0c0f3d]/50" />
                            <span className="font-sans text-xs text-[#0c0f3d]/70">{c.duration_minutes} min</span>
                        </div>
                        <div className="text-right">
                            {c.price === 0 ? (
                                <span className="font-serif text-2xl text-[#e47a6a]">Free</span>
                            ) : (
                                <span className="font-serif text-2xl text-[#0c0f3d]">${c.price.toFixed(0)}</span>
                            )}
                        </div>
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0c0f3d] group-hover:text-[#e47a6a]">
                        Select <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                </button>
            ))}
        </div>
    );
}

function StepPickTime({ type, slotsByDate, dates, selectedDate, setSelectedDate, onPickSlot, onBack, loading }) {
    const activeDate = selectedDate || dates[0];
    const slots = activeDate ? slotsByDate[activeDate] || [] : [];

    return (
        <div data-testid="booking-pick-time">
            <button
                onClick={onBack}
                data-testid="booking-back-btn"
                className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-[#0c0f3d]/60 hover:text-[#0c0f3d] mb-6"
            >
                <ArrowLeft size={12} /> Back
            </button>
            <div className="bg-white border border-[#e3c7b9] p-6 mb-8">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-[#e47a6a]/10">
                        <Calendar size={20} className="text-[#e47a6a]" />
                    </div>
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#0c0f3d]/60">Selected</p>
                        <h3 className="font-serif text-xl text-[#0c0f3d] mt-1">{type.name}</h3>
                        <p className="font-sans text-sm text-[#0c0f3d]/70 mt-1">{type.duration_minutes} min · {type.price === 0 ? "Free" : `$${type.price.toFixed(0)}`}</p>
                    </div>
                </div>
            </div>

            {loading ? (
                <p className="font-serif italic text-xl text-[#0c0f3d]">Loading available times…</p>
            ) : dates.length === 0 ? (
                <p className="font-serif italic text-xl text-[#0c0f3d]">No available times in the next 30 days. Please email <a href="mailto:shana@theconnectionguru.co" className="underline">shana@theconnectionguru.co</a>.</p>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
                    {/* Date list */}
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">Pick a date</p>
                        <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-2">
                            {dates.map((d) => {
                                const dt = new Date(d + "T12:00:00");
                                const label = dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
                                return (
                                    <button
                                        key={d}
                                        onClick={() => setSelectedDate(d)}
                                        data-testid={`booking-date-${d}`}
                                        className={`w-full text-left px-4 py-3 text-sm border transition-colors ${
                                            activeDate === d
                                                ? "bg-[#0c0f3d] text-white border-[#0c0f3d]"
                                                : "bg-white text-[#0c0f3d] border-[#e3c7b9] hover:border-[#0c0f3d]"
                                        }`}
                                    >
                                        <div className="font-sans">{label}</div>
                                        <div className={`font-sans text-[10px] uppercase tracking-[0.2em] mt-1 ${activeDate === d ? "text-white/60" : "text-[#0c0f3d]/50"}`}>
                                            {slotsByDate[d].length} {slotsByDate[d].length === 1 ? "slot" : "slots"}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Slot grid */}
                    <div>
                        <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] mb-3">
                            Times available {activeDate ? `on ${new Date(activeDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}` : ""} (Eastern time)
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {slots.map((s) => (
                                <button
                                    key={s.start_iso}
                                    onClick={() => onPickSlot(s)}
                                    data-testid={`booking-slot-${s.start_iso}`}
                                    className="px-4 py-4 border border-[#e3c7b9] bg-white text-[#0c0f3d] hover:border-[#0c0f3d] hover:bg-[#e3c7b9]/20 transition-colors"
                                >
                                    <span className="font-serif text-xl">{s.time_display}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StepConfirm({ type, slot, onBack, submitting, setSubmitting }) {
    const [form, setForm] = useState({
        client_name: "",
        client_email: "",
        client_organization: "",
        client_phone: "",
        notes: "",
    });

    const displayDate = new Date(slot.start_iso).toLocaleDateString("en-US", {
        weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
    const displayTime = new Date(slot.start_iso).toLocaleTimeString("en-US", {
        hour: "numeric", minute: "2-digit", timeZone: "America/New_York",
    });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.client_name || !form.client_email) {
            toast.error("Name and email required.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/consultations/${type.id}/book`, {
                consultation_id: type.id,
                start_iso: slot.start_iso,
                origin_url: window.location.origin,
                ...form,
            });
            if (data.free) {
                window.location.href = `/book/success?booking_id=${data.booking_id}&free=1`;
            } else if (data.checkout_url) {
                window.location.href = data.checkout_url;
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not book that time.";
            toast.error(typeof msg === "string" ? msg : "Booking failed.");
            setSubmitting(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10" data-testid="booking-confirm">
            <div>
                <button
                    onClick={onBack}
                    data-testid="booking-confirm-back-btn"
                    className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-[#0c0f3d]/60 hover:text-[#0c0f3d] mb-6"
                >
                    <ArrowLeft size={12} /> Change time
                </button>
                <h2 className="font-serif text-3xl md:text-4xl text-[#0c0f3d] leading-tight">Your details</h2>
                <p className="font-sans text-sm text-[#0c0f3d]/70 mt-3">Tell us who you are so Shana can prepare for the call.</p>

                <form onSubmit={submit} className="mt-8 space-y-5">
                    <div>
                        <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Full name *</label>
                        <input
                            required
                            value={form.client_name}
                            onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                            data-testid="booking-form-name"
                            className="w-full font-sans text-sm bg-white border border-[#e3c7b9] px-4 py-3 outline-none focus:border-[#0c0f3d]"
                        />
                    </div>
                    <div>
                        <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Work email *</label>
                        <input
                            required
                            type="email"
                            value={form.client_email}
                            onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                            data-testid="booking-form-email"
                            className="w-full font-sans text-sm bg-white border border-[#e3c7b9] px-4 py-3 outline-none focus:border-[#0c0f3d]"
                        />
                    </div>
                    <div>
                        <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Organization</label>
                        <input
                            value={form.client_organization}
                            onChange={(e) => setForm({ ...form, client_organization: e.target.value })}
                            data-testid="booking-form-org"
                            className="w-full font-sans text-sm bg-white border border-[#e3c7b9] px-4 py-3 outline-none focus:border-[#0c0f3d]"
                        />
                    </div>
                    <div>
                        <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Phone (optional)</label>
                        <input
                            value={form.client_phone}
                            onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                            data-testid="booking-form-phone"
                            className="w-full font-sans text-sm bg-white border border-[#e3c7b9] px-4 py-3 outline-none focus:border-[#0c0f3d]"
                        />
                    </div>
                    <div>
                        <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">What would you like to discuss?</label>
                        <textarea
                            rows={4}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            data-testid="booking-form-notes"
                            className="w-full font-sans text-sm bg-white border border-[#e3c7b9] px-4 py-3 outline-none focus:border-[#0c0f3d] resize-none"
                            placeholder="Tell Shana about your organization, goals, or specific topics."
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="booking-submit-btn"
                        className="group w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#e47a6a] transition-colors disabled:opacity-50"
                    >
                        {submitting ? "Processing…" : type.price === 0 ? "Confirm booking" : `Continue to payment · $${type.price.toFixed(0)}`}
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </button>

                    <p className="font-sans text-xs text-[#0c0f3d]/60 leading-[1.7]">
                        By booking, you agree to the terms and cancellation policy. Refunds available up to 24 hours before your scheduled time.
                    </p>
                </form>
            </div>

            {/* Summary sidebar */}
            <div className="bg-white border border-[#e3c7b9] p-8 h-fit lg:sticky lg:top-32" data-testid="booking-summary">
                <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#e47a6a] mb-4">Booking Summary</p>
                <h3 className="font-serif text-2xl text-[#0c0f3d] leading-tight">{type.name}</h3>
                <div className="mt-6 space-y-4 border-t border-[#e3c7b9] pt-6">
                    <SummaryRow icon={Calendar} label="When" value={<><div>{displayDate}</div><div className="text-[#0c0f3d]/60">{displayTime} ET</div></>} />
                    <SummaryRow icon={Clock} label="Duration" value={`${type.duration_minutes} minutes`} />
                    <SummaryRow icon={Video} label="Location" value="Zoom (link sent by email)" />
                    <SummaryRow icon={Users} label="With" value="Shana Price" />
                </div>
                <div className="mt-8 pt-6 border-t border-[#e3c7b9] flex items-baseline justify-between">
                    <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#0c0f3d]/60">Total</span>
                    <span className="font-serif text-3xl text-[#0c0f3d]">
                        {type.price === 0 ? "Free" : `$${type.price.toFixed(2)}`}
                    </span>
                </div>
            </div>
        </div>
    );
}

function SummaryRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <Icon size={14} className="text-[#e47a6a] mt-0.5" />
            <div className="flex-1">
                <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#0c0f3d]/50">{label}</p>
                <div className="font-sans text-sm text-[#0c0f3d] mt-1 leading-[1.5]">{value}</div>
            </div>
        </div>
    );
}

export function BookingSuccess() {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const bookingIdParam = params.get("booking_id");
    const isFree = params.get("free") === "1";
    const [state, setState] = useState({ loading: true });

    useEffect(() => {
        let active = true;
        const fetchById = async (id) => {
            try {
                const { data } = await axios.get(`${API}/consultations/booking/by-id/${id}`);
                if (active) setState({ loading: false, booking: data });
            } catch {
                if (active) setState({ loading: false, error: "Could not load your booking." });
            }
        };

        if (isFree && bookingIdParam) {
            fetchById(bookingIdParam);
            return () => { active = false; };
        }
        if (!sessionId) {
            setState({ loading: false, error: "Missing booking reference." });
            return;
        }
        let attempts = 0;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/consultations/booking/${sessionId}/status`);
                if (!active) return;
                if (data.status === "confirmed" || data.status === "paid") {
                    setState({ loading: false, booking: data.booking });
                    return;
                }
                if (attempts >= 20) {
                    setState({ loading: false, delayed: true, booking: data.booking });
                    return;
                }
                attempts++;
                setTimeout(poll, 2500);
            } catch {
                if (active) setState({ loading: false, error: "Could not confirm your booking." });
            }
        };
        poll();
        return () => { active = false; };
    }, [sessionId, bookingIdParam, isFree]);

    const b = state.booking;
    const dateStr = b ? new Date(b.start_iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "";
    const timeStr = b ? new Date(b.start_iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }) : "";

    return (
        <main className="bg-[#fbf9f8] min-h-screen pt-32 pb-24" data-testid="booking-success">
            <div className="max-w-[720px] mx-auto px-6 md:px-10">
                {state.loading ? (
                    <p className="font-serif italic text-2xl text-[#0c0f3d] text-center py-20">Confirming your booking…</p>
                ) : state.error ? (
                    <div className="text-center py-20">
                        <h1 className="font-serif text-3xl md:text-4xl text-[#0c0f3d]">{state.error}</h1>
                        <a href="/book" className="mt-8 inline-block px-8 py-3 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.25em]">Try again</a>
                    </div>
                ) : (
                    <>
                        <div className="text-center">
                            <CheckCircle2 size={56} className="text-[#e47a6a] mx-auto mb-6" />
                            <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#e47a6a] mb-4">Booking Confirmed</p>
                            <h1 className="font-serif text-4xl md:text-5xl text-[#0c0f3d] leading-[1.1] tracking-tight">
                                You&apos;re on <span className="italic text-[#e47a6a]">the calendar.</span>
                            </h1>
                            {b && (
                                <p className="font-sans text-base text-[#0c0f3d]/75 mt-6 leading-[1.8]">
                                    Hi {b.client_name.split(" ")[0]} — a confirmation with the Zoom link and calendar invite has been sent to <strong>{b.client_email}</strong>.
                                </p>
                            )}
                        </div>

                        {b && (
                            <div className="mt-12 bg-white border border-[#e3c7b9] p-8">
                                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#6d91ad] mb-4">Your booking</p>
                                <h2 className="font-serif text-2xl text-[#0c0f3d]">{b.consultation_name}</h2>
                                <div className="mt-6 space-y-4 border-t border-[#e3c7b9] pt-6">
                                    <SummaryRow icon={Calendar} label="When" value={<><div>{dateStr}</div><div className="text-[#0c0f3d]/60">{timeStr} ET · {b.consultation_duration} minutes</div></>} />
                                    <SummaryRow icon={Video} label="Zoom link" value={<a href={b.zoom_link} target="_blank" rel="noopener noreferrer" className="text-[#e47a6a] underline break-all">{b.zoom_link}</a>} />
                                    <SummaryRow icon={Users} label="With" value="Shana Price" />
                                </div>
                            </div>
                        )}

                        <div className="mt-10 flex flex-wrap gap-4 justify-center">
                            <a href="/" className="inline-block px-8 py-3 border border-[#0c0f3d] text-[#0c0f3d] text-xs uppercase tracking-[0.25em] hover:bg-[#0c0f3d] hover:text-white">
                                Return home
                            </a>
                            <a href="mailto:shana@theconnectionguru.co" className="inline-block px-8 py-3 bg-[#0c0f3d] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#e47a6a]">
                                Reschedule?
                            </a>
                        </div>

                        <p className="font-sans text-xs text-[#0c0f3d]/60 text-center mt-12 leading-[1.7]">
                            Please check your inbox (and spam folder). If you don&apos;t see the confirmation, email <a href="mailto:shana@theconnectionguru.co" className="underline">shana@theconnectionguru.co</a>.
                        </p>
                    </>
                )}
            </div>
        </main>
    );
}
